/**
 * @file 组件预览页（Showcase）
 * @description 集中展示主题与 `components/ui`；每项用 ShowcaseCard 挂 Prompt。
 *
 * @whenToUse 本地开发对照 Token / 控件效果（路由 `/showcase`）。
 * @whenNotToUse 不要把本页当业务首页。
 */

import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  CircleFadingArrowUp,
  Component,
  Eye,
  FormInput,
  LayoutDashboard,
  ListChecks,
  MousePointerClick,
  Palette,
  Pencil,
  RectangleHorizontal,
  RefreshCw,
  ScrollText,
  Search,
  TextCursorInput,
  ToggleLeft,
  Trash2,
  X,
} from 'lucide-react'
import buttonPrompt from '@/docs/components/button.md?raw'
import checkboxPrompt from '@/docs/components/checkbox.md?raw'
import dialogPrompt from '@/docs/components/dialog.md?raw'
import formFieldPrompt from '@/docs/components/form-field.md?raw'
import iconButtonPrompt from '@/docs/components/icon-button.md?raw'
import inputPrompt from '@/docs/components/input.md?raw'
import labelPrompt from '@/docs/components/label.md?raw'
import radioPrompt from '@/docs/components/radio.md?raw'
import scrollAreaPrompt from '@/docs/components/scroll-area.md?raw'
import searchInputPrompt from '@/docs/components/search-input.md?raw'
import selectPrompt from '@/docs/components/select.md?raw'
import gradeSelectPrompt from '@/docs/components/grade-select.md?raw'
import showcaseCardPrompt from '@/docs/components/showcase-card.md?raw'
import switchPrompt from '@/docs/components/switch.md?raw'
import textareaPrompt from '@/docs/components/textarea.md?raw'
import {
  DialogDemo,
  GradeSelectDemo,
  RadioDemo,
  SearchInputDemo,
  SelectDemo,
  SwitchDemo,
} from '@/components/pages/showcase-page/basic-demos.tsx'
import { ShowcaseExtraSections } from '@/components/pages/showcase-page/extra-sections.tsx'
import { ShowcaseFormAdvancedSections } from '@/components/pages/showcase-page/form-advanced-sections.tsx'
import { ShowcaseMoreControlsSections } from '@/components/pages/showcase-page/more-controls-sections.tsx'
import { ShowcaseP2Sections } from '@/components/pages/showcase-page/p2-sections.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { FloatButton } from '@/components/ui/float-button.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { IconButton } from '@/components/ui/icon-button.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import { ShowcaseCard } from '@/components/ui/showcase-card.tsx'
import { Textarea } from '@/components/ui/textarea.tsx'
import { ThemeSwitcher } from '@/components/ui/theme-switcher.tsx'
import { getTheme } from '@/lib/themes.ts'
import { useThemeStore } from '@/stores/theme-store.ts'

export function ShowcasePage() {
  const themeId = useThemeStore((state) => state.theme)
  const theme = getTheme(themeId)

  return (
    <div className="min-h-full bg-canvas text-foreground">
      <div className="mx-auto flex max-w-6xl flex-col gap-10 px-6 py-10">
        <header className="flex flex-col gap-4">
          <Link
            to="/"
            className="inline-flex w-fit items-center gap-1.5 text-sm text-muted transition-colors duration-150 hover:text-foreground"
          >
            <ArrowLeft className="size-4" aria-hidden />
            返回首页
          </Link>

          <div className="flex items-center gap-3">
            <LayoutDashboard className="size-7 text-primary" aria-hidden />
            <h1 className="text-2xl font-semibold tracking-tight">组件预览</h1>
          </div>

          <p className="max-w-2xl text-sm leading-relaxed text-muted">
            每个区块标题旁的眼睛可查看 Coding Prompt，并一键复制给 Agent。控件统一紧凑尺寸（h-9 / 38px），交互带过渡。
          </p>
        </header>

        <ShowcaseCard
          id="theme"
          title="主题"
          description={`当前：${theme.name} · ${theme.tagline}。点选色卡切换，选择会持久化。`}
          icon={<Palette className="size-4" aria-hidden />}
        >
          <ThemeSwitcher />
        </ShowcaseCard>

        <ShowcaseCard
          id="showcase-card"
          title="ShowcaseCard"
          description="预览卡本身：标题 + 眼睛 → Prompt 弹窗（点本卡眼睛即可）。"
          icon={<Eye className="size-4" aria-hidden />}
          prompt={showcaseCardPrompt}
        >
          <p className="text-sm text-muted">
            后续每个控件都使用本卡片包裹预览内容，并传入对应 `docs/components/*.md`。
          </p>
        </ShowcaseCard>

        <ShowcaseCard
          id="button"
          title="Button"
          description="紧凑按钮，无 size；含 primary / secondary / ghost / danger / loading。"
          icon={<MousePointerClick className="size-4" aria-hidden />}
          prompt={buttonPrompt}
        >
          <div className="flex flex-wrap items-center gap-3">
            <Button>Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="danger">Danger</Button>
            <Button loading>Loading</Button>
            <Button icon={<Component className="size-4" aria-hidden />}>
              With Icon
            </Button>
          </div>
        </ShowcaseCard>

        <ShowcaseCard
          id="icon-button"
          title="IconButton"
          description="仅图标方形按钮；ghost（hover 显背景）/ outline（边框）/ solid（实心）。"
          icon={<Pencil className="size-4" aria-hidden />}
          prompt={iconButtonPrompt}
        >
          <div className="flex flex-wrap items-center gap-3">
            <IconButton
              variant="ghost"
              aria-label="关闭"
              icon={<X className="size-4" aria-hidden />}
            />
            <IconButton
              variant="outline"
              aria-label="刷新"
              icon={<RefreshCw className="size-4" aria-hidden />}
            />
            <IconButton
              variant="solid"
              aria-label="编辑"
              icon={<Pencil className="size-4" aria-hidden />}
            />
            <IconButton
              variant="outline"
              aria-label="删除"
              loading
              icon={<Trash2 className="size-4" aria-hidden />}
            />
          </div>
        </ShowcaseCard>

        <ShowcaseCard
          id="label"
          title="Label"
          description="表单标签，支持必填星号。"
          icon={<TextCursorInput className="size-4" aria-hidden />}
          prompt={labelPrompt}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="demo-label">用户名</Label>
            <Label required>密码</Label>
          </div>
        </ShowcaseCard>

        <ShowcaseCard
          id="input"
          title="Input"
          description="单行输入 h-9；支持 invalid / 前后缀 / 密码显隐 / disabled。"
          icon={<FormInput className="size-4" aria-hidden />}
          prompt={inputPrompt}
        >
          <div className="grid max-w-md gap-3">
            <Input placeholder="默认输入" />
            <Input placeholder="校验失败" invalid />
            <Input placeholder="禁用" disabled />
            <Input
              placeholder="带前缀"
              prefix={<Search className="size-4" aria-hidden />}
            />
            <Input type="password" placeholder="密码（可显隐）" defaultValue="secret" />
            <Input
              type="password"
              placeholder="密码（无眼睛）"
              showPasswordToggle={false}
            />
          </div>
        </ShowcaseCard>

        <ShowcaseCard
          id="search-input"
          title="SearchInput"
          description="搜索框：图标 + 清除。"
          icon={<Search className="size-4" aria-hidden />}
          prompt={searchInputPrompt}
        >
          <SearchInputDemo />
        </ShowcaseCard>

        <ShowcaseCard
          id="textarea"
          title="Textarea"
          description="多行输入；支持 invalid。"
          icon={<TextCursorInput className="size-4" aria-hidden />}
          prompt={textareaPrompt}
        >
          <div className="max-w-md">
            <Textarea placeholder="请输入备注…" />
          </div>
        </ShowcaseCard>

        <ShowcaseCard
          id="select"
          title="Select"
          description="自定义下拉（非原生）；视口边界翻转 + ScrollArea + fade。选项较多时可滚动。"
          icon={<ListChecks className="size-4" aria-hidden />}
          prompt={selectPrompt}
        >
          <SelectDemo />
        </ShowcaseCard>

        <ShowcaseCard
          id="grade-select"
          title="GradeSelect"
          description="按教育阶段过滤年级；未选阶段时展示全部年级。"
          icon={<ListChecks className="size-4" aria-hidden />}
          prompt={gradeSelectPrompt}
        >
          <GradeSelectDemo />
        </ShowcaseCard>

        <ShowcaseCard
          id="checkbox"
          title="Checkbox"
          description="自定义复选框。"
          icon={<ListChecks className="size-4" aria-hidden />}
          prompt={checkboxPrompt}
        >
          <div className="flex flex-col gap-2">
            <Checkbox id="c1" label="接受协议" defaultChecked />
            <Checkbox id="c2" label="订阅通知" />
            <Checkbox id="c3" label="禁用项" disabled />
          </div>
        </ShowcaseCard>

        <ShowcaseCard
          id="radio"
          title="Radio"
          description="RadioGroup 互斥单选。"
          icon={<ListChecks className="size-4" aria-hidden />}
          prompt={radioPrompt}
        >
          <RadioDemo />
        </ShowcaseCard>

        <ShowcaseCard
          id="switch"
          title="Switch"
          description="布尔开关。"
          icon={<ToggleLeft className="size-4" aria-hidden />}
          prompt={switchPrompt}
        >
          <SwitchDemo />
        </ShowcaseCard>

        <ShowcaseCard
          id="form-field"
          title="FormField"
          description="Label + 控件 + hint/error 标准排布。"
          icon={<FormInput className="size-4" aria-hidden />}
          prompt={formFieldPrompt}
        >
          <div className="grid max-w-md gap-4">
            <FormField
              label="邮箱"
              htmlFor="email"
              required
              hint="用于登录与通知"
            >
              <Input id="email" type="email" placeholder="name@example.com" />
            </FormField>
            <FormField label="昵称" htmlFor="nick" error="昵称不能为空">
              <Input id="nick" invalid placeholder="请输入昵称" />
            </FormField>
          </div>
        </ShowcaseCard>

        <ShowcaseCard
          id="scroll-area"
          title="ScrollArea"
          description="自定义悬浮滚动条（不占内容空间）；默认 hover 显示。可试纵向 / 横向。"
          icon={<ScrollText className="size-4" aria-hidden />}
          prompt={scrollAreaPrompt}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="mb-2 text-xs text-muted">纵向 · showOn=hover</p>
              <ScrollArea className="h-40 rounded-xl border border-border bg-raised">
                <div className="space-y-2 p-3">
                  {Array.from({ length: 20 }, (_, i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-border px-3 py-2 text-sm"
                    >
                      列表项 {i + 1}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
            <div>
              <p className="mb-2 text-xs text-muted">横向 · showOn=always</p>
              <ScrollArea
                orientation="horizontal"
                showOn="always"
                className="h-24 rounded-xl border border-border bg-raised"
              >
                <div className="flex w-max gap-2 p-3">
                  {Array.from({ length: 12 }, (_, i) => (
                    <div
                      key={i}
                      className="flex h-16 w-28 shrink-0 items-center justify-center rounded-lg border border-border text-sm"
                    >
                      卡片 {i + 1}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
        </ShowcaseCard>

        <ShowcaseCard
          id="dialog"
          title="Dialog"
          description="模态弹层 + fade；内容区使用 ScrollArea。点下方按钮打开示例。"
          icon={<RectangleHorizontal className="size-4" aria-hidden />}
          prompt={dialogPrompt}
        >
          <DialogDemo />
        </ShowcaseCard>

        <ShowcaseExtraSections />
        <ShowcaseFormAdvancedSections />
        <ShowcaseMoreControlsSections />
        <ShowcaseP2Sections />

        <ShowcaseCard
          id="float-button"
          title="FloatButton"
          description="右下角悬浮按钮。开发环境在已登录页展示「组件预览」入口。"
          icon={<CircleFadingArrowUp className="size-4" aria-hidden />}
        >
          <div className="relative flex h-40 items-end justify-end rounded-xl border border-dashed border-border bg-raised p-4">
            <p className="absolute top-4 left-4 text-xs text-muted">
              预览容器内 static! 示意（默认 fixed 贴视口右下）
            </p>
            <FloatButton
              aria-label="示例悬浮按钮"
              label="FloatButton"
              className="static!"
              icon={<Component className="size-5" aria-hidden />}
            />
          </div>
        </ShowcaseCard>
      </div>
    </div>
  )
}
